/**
 * COD app config model (feature 0026 — COD as a real installable app).
 *
 * The seller-editable configuration for the Cash-on-Delivery order form, authored in the Form designer and
 * (later) read by the storefront + builder to render the real form. It is the SINGLE SOURCE OF TRUTH for the
 * whole form: its presentation, its fields, its conversion blocks, its copy, and its visual theme. Everything
 * a seller toggles in the Form designer lives here.
 *
 * Persisted opaquely under `store_settings.data['appConfig']['cod']` via `GET/PUT /api/store/apps/cod/config`
 * — so the SHAPE is owned here (and by the eventual storefront reader), not by the backend.
 *
 * {@link parseCodConfig} is defensive: it always returns every core field (in order) and every block with
 * defaults filled, drops malformed custom fields / offer tiers, and never lets a core field (name / phone) be
 * disabled — so a partial / legacy / hand-edited config can never leave the form half-populated. Pure +
 * framework-free → unit-tested directly.
 */
import { t } from '@aquilixshop/i18n';
import { DEFAULT_FORM_THEME, parseFormTheme, serializeFormTheme, } from './cod-theme';
/** A Material Symbols token (lowercase letters, digits, underscores) — else the field has no icon. */
const ICON_TOKEN = /^[a-z0-9_]+$/;
/**
 * The country the form's phone field speaks: the seller's own choice when they made one, else the store's
 * country, else nothing (no prefix box, no flag — never a guessed `+212`).
 *
 * There used to be no choice at all: the storefront derived the dial code from the store country and the
 * designer's preview painted a hardcoded Moroccan flag beside it. A seller selling in Romania had no way
 * to correct either one.
 */
export function phoneCountryOf(config, storeCountryFallback) {
    const authored = countryCode(config.settings.phoneCountry);
    if (authored)
        return authored;
    return countryCode(config.settings.storeCountry) || countryCode(storeCountryFallback);
}
/**
 * Whether the Moroccan phone-shape rule applies to THIS form: the seller left it on AND the form's phone
 * country is Morocco. A shop selling in Bucharest with the default rule still on would otherwise reject
 * every number its buyers can type.
 */
export function moroccanPhoneRuleApplies(config, storeCountryFallback) {
    return (config.settings.validateMoroccanPhone && phoneCountryOf(config, storeCountryFallback) === 'MA');
}
/** The built-in fields with their fixed types (French labels — the COD audience is Moroccan). */
const CORE_FIELDS = [
    {
        id: 'name',
        type: 'text',
        label: 'Nom complet',
        placeholder: 'Votre nom',
        enabled: true,
        required: true,
        core: true,
        icon: 'person',
    },
    {
        // No hardcoded dial code: the storefront derives the placeholder example from the store's selling
        // country (Store.countryCode) so a non-MA store never shows "+212". Empty = "derive at render".
        id: 'phone',
        type: 'phone',
        label: 'Téléphone',
        placeholder: '',
        enabled: true,
        required: true,
        core: true,
        icon: 'call',
    },
    {
        id: 'city',
        type: 'city',
        label: 'Ville',
        placeholder: 'Votre ville',
        enabled: true,
        required: true,
        core: true,
        icon: 'location_on',
    },
    {
        id: 'address',
        type: 'address',
        label: 'Adresse',
        placeholder: 'Rue, quartier, étage, point de repère…',
        enabled: true,
        required: true,
        core: true,
        icon: 'home',
    },
    {
        id: 'note',
        type: 'textarea',
        label: 'Note (facultatif)',
        placeholder: 'Instructions de livraison…',
        enabled: false,
        required: false,
        core: true,
        icon: 'edit_note',
    },
];
const CORE_FIELD_BY_ID = new Map(CORE_FIELDS.map((f) => [f.id, f]));
/** The conversion blocks in display order, with their out-of-the-box on/off. */
const DEFAULT_BLOCKS = [
    { key: 'product', enabled: true },
    { key: 'offers', enabled: true },
    { key: 'bump', enabled: false },
    { key: 'shipping', enabled: true },
    { key: 'summary', enabled: true },
    { key: 'trust', enabled: true },
];
const DEFAULT_OFFERS = {
    title: 'Choisissez votre pack',
    tiers: [
        {
            id: 't1',
            qty: 1,
            discountType: 'none',
            discountValue: 0,
            label: '1 article',
            badge: '',
            highlight: false,
        },
        {
            id: 't2',
            qty: 2,
            discountType: 'percent',
            discountValue: 10,
            label: '2 articles',
            badge: 'Le + populaire',
            highlight: true,
        },
        {
            id: 't3',
            qty: 3,
            discountType: 'percent',
            discountValue: 20,
            label: '3 articles',
            badge: '',
            highlight: false,
        },
    ],
};
const DEFAULT_BUMP = {
    title: 'Ajoutez une garantie 2 ans',
    priceLabel: '+49 dh',
    description: 'Protection casse & défauts, sans conditions.',
    preChecked: false,
};
const DEFAULT_SETTINGS = {
    showOnProductPage: true,
    replaceAddToCart: true,
    stickyButton: true,
    direction: 'ltr',
    validateMoroccanPhone: true,
    phoneCountry: '',
    storeCountry: '',
};
const LAYOUTS = ['popup', 'embedded'];
const DISCOUNT_TYPES = ['none', 'percent', 'fixed'];
const FIELD_TYPES = [
    'text',
    'phone',
    'email',
    'number',
    'city',
    'address',
    'textarea',
    'select',
];
/** Field types a seller may pick when ADDING a custom field (city/address are core-only). */
export const CUSTOM_FIELD_TYPES = [
    { type: 'text', label: 'Texte' },
    { type: 'phone', label: 'Téléphone' },
    { type: 'email', label: 'Email' },
    { type: 'number', label: 'Nombre' },
    { type: 'textarea', label: 'Zone de texte' },
    { type: 'select', label: 'Liste déroulante' },
];
/** The out-of-the-box config a store starts from. */
/**
 * The starting config in a given language. `DEFAULT_COD_CONFIG` below is this one in French, kept as a
 * named export because a dozen callers import it and only the ones that KNOW a language need the function.
 */
export function defaultCodConfig(locale) {
    const ctx = { locale: locale ?? 'fr' };
    const fields = CORE_FIELDS.map((f) => {
        const key = FIELD_TEXT_KEYS[f.id];
        if (!key)
            return f;
        const label = t(ctx, key.label);
        return key.placeholder
            ? { ...f, label, placeholder: t(ctx, key.placeholder) }
            : { ...f, label };
    });
    return {
        ...DEFAULT_COD_CONFIG,
        fields,
        offers: {
            ...DEFAULT_OFFERS,
            title: t(ctx, 'cod.default_offers_title'),
            // A tier's label COUNTS something, so it is a plural, not a sentence with a number glued on: Arabic
            // has six forms of it. The badge is the seller's first "most popular" flag, and was French too.
            tiers: DEFAULT_OFFERS.tiers.map((tier) => ({
                ...tier,
                label: t(ctx, 'cart.item_count', { count: tier.qty }),
                badge: tier.badge ? t(ctx, 'cod.default_offer_badge') : tier.badge,
            })),
        },
        bump: {
            ...DEFAULT_BUMP,
            title: t(ctx, 'cod.default_bump_title'),
            description: t(ctx, 'cod.default_bump_description'),
        },
        buttonLabel: t(ctx, 'cod.default_button_label'),
        confirmationMessage: t(ctx, 'cod.default_confirmation_message'),
    };
}
/** Which dictionary keys carry each core field's own two sentences. */
const FIELD_TEXT_KEYS = {
    name: { label: 'cod.default_field_name', placeholder: 'cod.default_name_placeholder' },
    // Phone has NO placeholder key on purpose: the storefront derives the dial-code example from the
    // store's selling country, so a fixed sentence here would fight it. Only the label is a sentence.
    phone: { label: 'cod.default_field_phone' },
    city: { label: 'cod.default_field_city', placeholder: 'cod.default_city_placeholder' },
    address: { label: 'cod.default_field_address', placeholder: 'cod.default_address_placeholder' },
    note: { label: 'cod.default_field_note', placeholder: 'cod.default_note_placeholder' },
};
export const DEFAULT_COD_CONFIG = {
    // Fail-open: a store with no config at all still gets its COD funnel (the gate only ever turns it OFF, and
    // only on an explicit `enabled:false` from the backend's install status).
    enabled: true,
    // `embedded` (the form is always visible in the buy-box) is the higher-converting default for a COD funnel —
    // no extra click gate. `popup` (trigger button → modal) and `full` are opt-in per store.
    layout: 'embedded',
    fields: CORE_FIELDS,
    blocks: DEFAULT_BLOCKS,
    offers: DEFAULT_OFFERS,
    bump: DEFAULT_BUMP,
    settings: DEFAULT_SETTINGS,
    buttonLabel: 'Commander — Paiement à la livraison',
    confirmationMessage: 'Merci ! Votre commande est confirmée. Nous vous appellerons pour confirmer la livraison.',
    requirePhoneConfirmation: true,
    theme: DEFAULT_FORM_THEME,
};
function isRecord(v) {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}
/** A non-blank string wins; anything else falls back (a cleared label is meaningless). */
function str(v, fallback) {
    return typeof v === 'string' && v.trim().length > 0 ? v : fallback;
}
/** Any string wins, including empty (placeholders / badges may legitimately be blank). */
function optStr(v, fallback) {
    return typeof v === 'string' ? v : fallback;
}
function bool(v, fallback) {
    return typeof v === 'boolean' ? v : fallback;
}
function num(v, fallback) {
    return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}
/**
 * An ISO-3166 alpha-2 code, upper-cased; anything else (absent, a full name, a 3-letter code) → `''`,
 * which every reader treats as "not set" rather than guessing a country for the seller.
 */
function countryCode(v) {
    const c = typeof v === 'string' ? v.trim().toUpperCase() : '';
    return /^[A-Z]{2}$/.test(c) ? c : '';
}
function oneOf(v, allowed, fallback) {
    return typeof v === 'string' && allowed.includes(v) ? v : fallback;
}
function options(v) {
    return Array.isArray(v) ? v.filter((o) => typeof o === 'string') : [];
}
/** A stored icon → a valid Material Symbols token, an explicit '' (seller cleared it), else the fallback. */
function iconOr(v, fallback) {
    if (typeof v === 'string')
        return ICON_TOKEN.test(v) ? v : '';
    return fallback;
}
/**
 * Parse the stored `fields` into a complete list: core fields (relabel / toggle / require overlaid by id,
 * type + core flag fixed, name/phone forced on) plus valid custom fields, de-duplicated by id and preserving
 * the stored order. Any core field missing from the stored config is appended so the list is always complete.
 */
function parseFields(raw, d) {
    const out = [];
    const seen = new Set();
    if (Array.isArray(raw)) {
        for (const item of raw) {
            if (!isRecord(item) || typeof item['id'] !== 'string')
                continue;
            const id = item['id'];
            if (seen.has(id))
                continue;
            const coreDef = CORE_FIELD_BY_ID.get(id);
            if (coreDef) {
                seen.add(id);
                out.push({
                    ...coreDef,
                    label: str(item['label'], coreDef.label),
                    placeholder: optStr(item['placeholder'], coreDef.placeholder),
                    // Every field is the seller's to show or hide — including name / phone / city / address, which
                    // used to be forced on here because the checkout contract rejected a blank address. The backend
                    // now accepts an order whose form never asked for one (21/10/2026), so the lock became a cage:
                    // a seller selling a digital product could not take the street address off their own form.
                    enabled: bool(item['enabled'], coreDef.enabled),
                    required: bool(item['required'], coreDef.required),
                    icon: iconOr(item['icon'], coreDef.icon ?? ''),
                });
            }
            else {
                seen.add(id);
                const type = oneOf(item['type'], FIELD_TYPES, 'text');
                out.push({
                    id,
                    type,
                    label: str(item['label'], 'Nouveau champ'),
                    placeholder: optStr(item['placeholder'], ''),
                    enabled: bool(item['enabled'], true),
                    required: bool(item['required'], false),
                    core: false,
                    icon: iconOr(item['icon'], ''),
                    ...(type === 'select' ? { options: options(item['options']) } : {}),
                });
            }
        }
    }
    for (const def of d.fields) {
        if (!seen.has(def.id))
            out.push(def);
    }
    return out;
}
/** Parse the stored `blocks` — every block key present in canonical order, `enabled` overlaid where valid. */
function parseBlocks(raw) {
    const stored = new Map();
    if (Array.isArray(raw)) {
        for (const b of raw) {
            if (isRecord(b) && typeof b['key'] === 'string')
                stored.set(b['key'], b);
        }
    }
    return DEFAULT_BLOCKS.map((def) => {
        const s = stored.get(def.key);
        return s ? { key: def.key, enabled: bool(s['enabled'], def.enabled) } : def;
    });
}
function parseOffers(raw, d) {
    const obj = isRecord(raw) ? raw : {};
    const rawTiers = obj['tiers'];
    const tiers = [];
    if (Array.isArray(rawTiers)) {
        rawTiers.forEach((t, i) => {
            if (!isRecord(t))
                return;
            tiers.push({
                id: typeof t['id'] === 'string' ? t['id'] : `t${i + 1}`,
                qty: Math.max(1, Math.round(num(t['qty'], 1))),
                discountType: oneOf(t['discountType'], DISCOUNT_TYPES, 'none'),
                discountValue: Math.max(0, num(t['discountValue'], 0)),
                label: str(t['label'], `${Math.max(1, Math.round(num(t['qty'], 1)))} articles`),
                badge: optStr(t['badge'], ''),
                highlight: bool(t['highlight'], false),
            });
        });
    }
    return {
        title: str(obj['title'], d.offers.title),
        tiers: tiers.length > 0 ? tiers : d.offers.tiers,
    };
}
function parseBump(raw, d) {
    const obj = isRecord(raw) ? raw : {};
    return {
        title: str(obj['title'], d.bump.title),
        priceLabel: str(obj['priceLabel'], d.bump.priceLabel),
        description: optStr(obj['description'], d.bump.description),
        preChecked: bool(obj['preChecked'], DEFAULT_BUMP.preChecked),
    };
}
function parseSettings(raw) {
    const obj = isRecord(raw) ? raw : {};
    return {
        showOnProductPage: bool(obj['showOnProductPage'], DEFAULT_SETTINGS.showOnProductPage),
        replaceAddToCart: bool(obj['replaceAddToCart'], DEFAULT_SETTINGS.replaceAddToCart),
        stickyButton: bool(obj['stickyButton'], DEFAULT_SETTINGS.stickyButton),
        direction: oneOf(obj['direction'], ['ltr', 'rtl'], 'ltr'),
        validateMoroccanPhone: bool(obj['validateMoroccanPhone'], DEFAULT_SETTINGS.validateMoroccanPhone),
        phoneCountry: countryCode(obj['phoneCountry']),
        storeCountry: countryCode(obj['storeCountry']),
    };
}
/**
 * Parse the seller's COD config, filling anything they have not written with a default IN THEIR SHOP'S
 * LANGUAGE (feature 0050 lot 2).
 *
 * The defaults are the sentences a seller finds when they open the app for the first time, and they were
 * French whatever the shop's language — so an Arabic shop's buy-box said "Commander — Paiement à la
 * livraison" on its button until the seller noticed and retyped it. [locale] is optional and defaults to
 * French, because three of the four callers had no reason to know a language until now.
 */
export function parseCodConfig(raw, locale) {
    const obj = isRecord(raw) ? raw : {};
    const d = defaultCodConfig(locale);
    return {
        // Only an EXPLICIT `false` turns the app off (see CodConfig.enabled) — anything else, including an absent
        // key on an older bundle, keeps the COD funnel alive.
        enabled: obj['enabled'] !== false,
        layout: oneOf(obj['layout'], LAYOUTS, 'embedded'),
        fields: parseFields(obj['fields'], d),
        blocks: parseBlocks(obj['blocks']),
        offers: parseOffers(obj['offers'], d),
        bump: parseBump(obj['bump'], d),
        settings: parseSettings(obj['settings']),
        buttonLabel: str(obj['buttonLabel'], d.buttonLabel),
        confirmationMessage: str(obj['confirmationMessage'], d.confirmationMessage),
        requirePhoneConfirmation: bool(obj['requirePhoneConfirmation'], DEFAULT_COD_CONFIG.requirePhoneConfirmation),
        theme: parseFormTheme(obj['theme']),
    };
}
/**
 * Serialize to the plain JSON object the PUT body expects (well under the backend's 100-entry cap).
 *
 * `enabled` is deliberately OMITTED: it is derived from the app's install status, not authored here, and the
 * backend treats a saved `enabled:true` as an activation intent — writing it back would let a Form-Designer save
 * silently re-install a COD app the seller had turned off in Apps.
 */
export function serializeCodConfig(config) {
    return {
        layout: config.layout,
        fields: config.fields.map((f) => ({
            id: f.id,
            type: f.type,
            label: f.label,
            placeholder: f.placeholder,
            enabled: f.enabled,
            required: f.required,
            core: f.core,
            ...(f.icon ? { icon: f.icon } : {}),
            ...(f.options ? { options: [...f.options] } : {}),
        })),
        blocks: config.blocks.map((b) => ({ key: b.key, enabled: b.enabled })),
        offers: {
            title: config.offers.title,
            tiers: config.offers.tiers.map((t) => ({
                id: t.id,
                qty: t.qty,
                discountType: t.discountType,
                discountValue: t.discountValue,
                label: t.label,
                badge: t.badge,
                highlight: t.highlight,
            })),
        },
        bump: { ...config.bump },
        // `direction` is DERIVED from the store language (see CodSettings.direction), so it is stripped on the way
        // out exactly like `enabled`: saving the Form designer must never freeze a direction into the blob that a
        // later language change would then contradict.
        settings: withoutDerived(config.settings),
        buttonLabel: config.buttonLabel,
        confirmationMessage: config.confirmationMessage,
        requirePhoneConfirmation: config.requirePhoneConfirmation,
        theme: serializeFormTheme(config.theme),
    };
}
/** Drop the settings the backend derives, so a save never writes them back. */
function withoutDerived(settings) {
    const { direction: _dir, storeCountry: _country, ...authored } = settings;
    return authored;
}
/** A blank custom field of the given type, with an id unique among `existing`. */
export function makeCustomField(type, existing, locale) {
    const ids = new Set(existing.map((f) => f.id));
    let n = existing.length + 1;
    let id = `custom-${n}`;
    while (ids.has(id)) {
        n += 1;
        id = `custom-${n}`;
    }
    const ctx = { locale: locale ?? 'fr' };
    const labels = {
        text: t(ctx, 'cod.field_type_text'),
        phone: t(ctx, 'cod.default_field_phone'),
        email: t(ctx, 'cod.field_type_email'),
        number: t(ctx, 'cod.field_type_number'),
        textarea: t(ctx, 'cod.field_type_textarea'),
        select: t(ctx, 'cod.field_type_select'),
    };
    return {
        id,
        type,
        label: labels[type] ?? t(ctx, 'cod.field_type_new'),
        placeholder: '',
        enabled: true,
        required: false,
        core: false,
        icon: '', // custom fields start with no icon; the seller picks any Material Symbols glyph
        ...(type === 'select' ? { options: ['Option 1', 'Option 2'] } : {}),
    };
}
/** A blank offer tier with an id unique among `existing`. */
export function makeOfferTier(existing) {
    const ids = new Set(existing.map((t) => t.id));
    let n = existing.length + 1;
    let id = `t${n}`;
    while (ids.has(id)) {
        n += 1;
        id = `t${n}`;
    }
    const qty = existing.length + 1;
    return {
        id,
        qty,
        discountType: 'percent',
        discountValue: 10,
        label: `${qty} articles`,
        badge: '',
        highlight: false,
    };
}
export function isBlockEnabled(config, key) {
    return config.blocks.find((b) => b.key === key)?.enabled ?? false;
}
/**
 * A pragmatic "is this an email address" check — the SAME bar the backend's `@Email` clears, no stricter.
 *
 * Deliberately lenient: the point is not to police addresses, it is to never lose an order because the
 * buyer typed `rim@` in an OPTIONAL field. Anything that fails here stays a note line (see
 * {@link buyerEmailOf}) instead of being sent as `customer.email`, where the backend would 400 the order.
 */
export function looksLikeEmail(value) {
    const v = value.trim();
    // `@Size(max = 200)` on the backend DTO — a longer address would 400 the whole order.
    return v.length <= 200 && /^[^\s@]+@[^\s@]+$/.test(v);
}
/**
 * The buyer's email as typed in THIS form, or '' when the seller asks for none.
 *
 * A COD form has no core email field: a seller who wants one ADDS a field of type `email`, which gets a
 * generated id (`custom-6`, …). Nothing downstream can guess that id, so until 16/10/2026 the address the
 * buyer typed was folded into the order's free-text note — `orders.customer_email` stayed NULL, the order
 * page showed "—", and every journey hung on that order was skipped with NO_EMAIL. Measured on the live
 * store `blossiti`: 15 orders, 3 with an email.
 *
 * The field's TYPE is the contract, not its id or its label: the first enabled `email` field holding
 * something that {@link looksLikeEmail} accepts wins. Returns '' when there is none — an absent email is
 * ordinary for a COD funnel, never an error.
 */
export function buyerEmailOf(config, form) {
    for (const f of config.fields) {
        if (f.type !== 'email' || !f.enabled)
            continue;
        const typed = (form[f.id] ?? '').trim();
        if (looksLikeEmail(typed))
            return typed;
    }
    return '';
}
//# sourceMappingURL=cod-config.js.map